import { API_BASE } from "../config";
// =============================================================================
// File: frontend/src/components/DietComplianceChecker.jsx
// Diet Compliance Checker - Check Products Against Dietary Preferences
// Patent claims exercised: P-003 Claims 15-17 (nutritional analysis and comparison)
// =============================================================================

import React, { useState, useEffect } from 'react';
import MedicalDisclaimer from './MedicalDisclaimer';
import { CitationsBlock } from './Citations';
import EdamamAttribution from './EdamamAttribution';

const DietComplianceChecker = ({
  token,
  fetchWithAuth,
  productData = null,
  upc = null,
  onClose,
  onComplianceResult,
  onProductChange,
  embedded = false
}) => {
  const authFetch = fetchWithAuth || ((url, opts) => fetch(url, { ...opts, headers: { ...opts?.headers, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }));
  const [product, setProduct] = useState(productData);
  const [complianceResult, setComplianceResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualUpc, setManualUpc] = useState(upc || '');
  const [strictness, setStrictness] = useState('moderate');

  useEffect(() => {
    if (productData) {
      setProduct(productData);
      checkCompliance(productData);
    } else if (upc) {
      lookupAndCheck(upc);
    }
  }, [productData, upc]);

  const lookupAndCheck = async (upcCode) => {
    setLoading(true);
    setError('');

    try {
      const lookupResponse = await authFetch(`${API_BASE}/api/products/lookup/upc/${upcCode}`);

      if (!lookupResponse.ok) {
        throw new Error('Product not found');
      }

      const lookupData = await lookupResponse.json();

      if (!lookupData.found) {
        setError('Product not found in our databases');
        return;
      }

      setProduct(lookupData.product);

      // Notify parent of product change
      if (onProductChange) {
        onProductChange({
          ...lookupData.product,
          upc: upcCode
        });
      }

      await checkCompliance(lookupData.product, upcCode);

    } catch (err) {
      console.error('Lookup error:', err);
      setError(err.message || 'Failed to look up product');
    } finally {
      setLoading(false);
    }
  };

  const checkCompliance = async (productToCheck, upcCode = null) => {
    setLoading(true);

    try {
      // Backend expects { upc: "..." } not the whole product object
      const requestBody = {
        upc: upcCode || productToCheck.upc || productToCheck.barcode || manualUpc
      };

      const response = await authFetch(`${API_BASE}/api/products/check-compliance`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to check compliance');
      }

      const data = await response.json();

      // Ensure diet_fit_percent has a valid number (default to 100 if missing)
      const result = {
        ...data,
        diet_fit_percent: typeof data.diet_fit_percent === 'number' ? data.diet_fit_percent : 100,
        strictness: data.strictness || strictness
      };

      setComplianceResult(result);
      setStrictness(result.strictness);

      // Notify parent of result
      if (onComplianceResult) {
        onComplianceResult(result);
      }

    } catch (err) {
      console.error('Compliance check error:', err);
      setError(err.message);
      // Set a default compliant result on error
      const defaultResult = {
        compliant: true,
        issues: [],
        warnings: [],
        diet_fit_percent: 100,
        strictness: strictness
      };
      setComplianceResult(defaultResult);
      if (onComplianceResult) {
        onComplianceResult(defaultResult);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStrictnessChange = async (newStrictness) => {
    setStrictness(newStrictness);

    // Update user's strictness setting in database
    try {
      await authFetch(`${API_BASE}/api/products/strictness?strictness=${newStrictness}`, {
        method: 'PUT'
      });
    } catch (err) {
      console.error('Failed to update strictness:', err);
    }

    // Re-check compliance with new strictness
    if (product) {
      await checkCompliance(product);
    }
  };

  const handleManualLookup = () => {
    if (manualUpc.trim()) {
      lookupAndCheck(manualUpc.trim());
    }
  };

  const handleCheckAnother = () => {
    setProduct(null);
    setComplianceResult(null);
    setManualUpc('');
    setError('');
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': '#DC2626',
      'high': '#F97316',
      'medium': '#F59E0B',
      'low': '#3B82F6'
    };
    return colors[severity] || '#6B7280';
  };

  const getSeverityBg = (severity) => {
    const colors = {
      'critical': '#FEE2E2',
      'high': '#FFEDD5',
      'medium': '#FEF3C7',
      'low': '#DBEAFE'
    };
    return colors[severity] || '#F3F4F6';
  };

  // GREEN for good (>=80), BLUE for okay (>=60), AMBER for medium (>=40), RED only for poor (<40)
  const getDietFitColor = (percent) => {
    const p = Number(percent) || 0;
    if (p >= 80) return '#059669';  // Success
    if (p >= 60) return '#3B82F6';  // Blue
    if (p >= 40) return '#F59E0B';  // Amber
    return '#EF4444';               // Red
  };

  const getGradeInfo = (grade) => {
    const grades = {
      'A': { color: '#059669', bg: '#D1FAE5' },
      'B': { color: '#3B82F6', bg: '#DBEAFE' },
      'C': { color: '#F59E0B', bg: '#FEF3C7' },
      'D': { color: '#F97316', bg: '#FFEDD5' },
      'F': { color: '#EF4444', bg: '#FEE2E2' }
    };
    return grades[grade] || grades['C'];
  };

  return (
    <div className={embedded ? 'dcc-container dcc-container--embedded' : 'dcc-container'}>
      <div className="dcc-header">
        <h3 className="dcc-title">Diet Compliance Check</h3>
        {!embedded && onClose && (
          <button className="dcc-closeButton" onClick={onClose}>{'\u00D7'}</button>
        )}
      </div>

      {/* Manual UPC Input - only if no product */}
      {!product && !loading && (
        <div className="dcc-inputRow">
          <input
            type="text"
            className="dcc-input"
            placeholder="Enter UPC barcode..."
            value={manualUpc}
            onChange={(e) => setManualUpc(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleManualLookup()}
          />
          <button className="dcc-checkButton" onClick={handleManualLookup}>
            Check
          </button>
        </div>
      )}

      {error && (
        <div className="dcc-error">{error}</div>
      )}

      {loading && (
        <div className="dcc-loading">
          Checking compliance...
        </div>
      )}

      {/* Product Info */}
      {product && (
        <div className="dcc-productCard">
          {product.image_url && (
            <img
              src={product.image_url}
              alt={product.label}
              className="dcc-productImage"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="dcc-productInfo">
            <div className="dcc-productName">{product.label}</div>
            {product.brand && (
              <div className="dcc-productBrand">{product.brand}</div>
            )}
            <EdamamAttribution />
          </div>
        </div>
      )}

      {/* Strictness Selector - shown when product is loaded */}
      {product && (
        <div className="dcc-strictnessRow">
          <span>Strictness:</span>
          <div className="dcc-strictnessButtons">
            {['relaxed', 'moderate', 'strict'].map(level => (
              <button
                key={level}
                onClick={() => handleStrictnessChange(level)}
                className={(complianceResult?.strictness || strictness) === level ? 'dcc-strictnessButton dcc-strictnessButton--active' : 'dcc-strictnessButton'}
              >
                {level}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Compliance Result */}
      {complianceResult && (
        <>
          {/* Main Compliance Badge */}
          <div className="dcc-badgeRow">
            <div className={
              complianceResult.compliant
                ? 'dcc-badge dcc-badge--compliant'
                : complianceResult.issues?.length > 0
                  ? 'dcc-badge dcc-badge--fail'
                  : 'dcc-badge dcc-badge--warning'
            }>
              {complianceResult.compliant ? (
                'Fits Your Diet'
              ) : complianceResult.issues?.length > 0 ? (
                'Does Not Fit'
              ) : (
                'Check Warnings'
              )}
            </div>
          </div>

          {/* Diet Fit Meter */}
          <div className="dcc-dietFitMeter">
            <div className="dcc-dietFitLabel">
              <span className="dcc-dietFitLabelText">Diet Fit Score</span>
              <span className="dcc-dietFitPercent" style={{ color: getDietFitColor(complianceResult.diet_fit_percent) }}>
                {complianceResult.diet_fit_percent || 100}%
              </span>
            </div>
            <div className="dcc-dietFitBar">
              <div className="dcc-dietFitFill" style={{ width: `${complianceResult.diet_fit_percent || 100}%`, backgroundColor: getDietFitColor(complianceResult.diet_fit_percent) }} />
            </div>
          </div>

          {/* Issues - Auto-shown */}
          {complianceResult.issues && complianceResult.issues.length > 0 && (
            <div className="dcc-issuesList">
              <h4 className="dcc-issuesTitle">
                Issues ({complianceResult.issues.length})
              </h4>
              {complianceResult.issues.map((issue, idx) => (
                <div
                  key={idx}
                  className="dcc-issueCard"
                  style={{ backgroundColor: getSeverityBg(issue.severity) }}
                >
                  <span className="dcc-issueIcon">
                    {issue.severity === 'critical' ? '\u{1F6AB}' :
                     issue.severity === 'high' ? '\u26D4' : '\u26A0\uFE0F'}
                  </span>
                  <div className="dcc-issueContent">
                    <div className="dcc-issueType" style={{ color: getSeverityColor(issue.severity) }}>
                      {issue.type}
                    </div>
                    <div className="dcc-issueMessage">{issue.message}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Warnings - Auto-shown, no click required */}
          {complianceResult.warnings && complianceResult.warnings.length > 0 && (
            <div className="dcc-warningsSection">
              <h4 className="dcc-warningsTitle">
                Warnings ({complianceResult.warnings.length})
              </h4>
              {complianceResult.warnings.map((warning, idx) => (
                <div key={idx} className="dcc-warningCard">
                  <span>{'\u26A1'}</span>
                  <span>{typeof warning === 'string' ? warning : warning.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* Apple 1.4.1 (G6, 2026-10-17): the sources behind this verdict. The full-UHC
              reasons (meds/conditions/labs) are already folded into the issues/warnings above;
              their citations come back in uhc_compatibility.reasons[].source_citation. */}
          <CitationsBlock
            sources={(complianceResult.uhc_compatibility?.reasons || [])
              .map(r => r && r.source_citation).filter(Boolean)}
            featureName="Diet Compliance Check"
            itemTitle={product?.label || product?.name || 'this product'}
            testId="diet-compliance-citations"
          />

          {/* Quick Nutrition - if available */}
          {product && (product.kcal_per_100g || product.protein_per_100g) && (
            <div className="dcc-nutritionGrid">
              <div className="dcc-nutritionItem">
                <div className="dcc-nutritionValue">
                  {Math.round(product.kcal_per_100g || 0)}
                </div>
                <div className="dcc-nutritionLabel">kcal/100g</div>
              </div>
              <div className="dcc-nutritionItem">
                <div className="dcc-nutritionValue">
                  {Math.round(product.protein_per_100g || 0)}g
                </div>
                <div className="dcc-nutritionLabel">protein</div>
              </div>
              <div className="dcc-nutritionItem">
                <div className="dcc-nutritionValue">
                  {Math.round(product.carbs_per_100g || 0)}g
                </div>
                <div className="dcc-nutritionLabel">carbs</div>
              </div>
              <div className="dcc-nutritionItem">
                <div className="dcc-nutritionValue">
                  {Math.round(product.fat_per_100g || 0)}g
                </div>
                <div className="dcc-nutritionLabel">fat</div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Check Another Product */}
      {(product || complianceResult) && (
        <button
          onClick={handleCheckAnother}
          className="dcc-checkAnotherButton"
        >
          Check Another Product
        </button>
      )}

      <MedicalDisclaimer variant="health" />
    </div>
  );
};

export default DietComplianceChecker;
